/**
 * 성별 표기 정규화 (2026-08-25).
 *
 * `patients.gender`에는 유입 경로마다 다른 표기가 섞여 들어온다:
 *   - `"M"` · `"F"`          — 초기 데이터
 *   - `"남"` · `"여"`         — 환자 등록·수정 모달(AddPatientModal·EditPatientModal)
 *   - `"남성"` · `"여성"`      — 케어링노트 파트너 API(`survey.gender`, 계약 §7이 이 두 값으로 고정)
 *
 * 표시 코드가 저마다 일부만 매핑해 온 탓에, 케어링노트 경유 환자는 보고서 머리말 성별이
 * `-`로 떨어졌다(늘픔가치 2026-08-24 문의). 표기 판정을 이 파일 하나로 모은다.
 *
 * ⚠️ 저장값을 바꾸지 않는다 — 표시 계층 전용이다. 분석 프롬프트에는 원문이 그대로 들어가야 한다
 *    (백엔드가 `survey.gender` 원문을 `<성별>`에 넣는다).
 */

export type NormalizedGender = "MALE" | "FEMALE";

/** 알려진 표기 → 정규값. 키는 trim + 대문자화한 값으로 조회한다. */
const GENDER_LOOKUP: Record<string, NormalizedGender> = {
  M: "MALE",
  MALE: "MALE",
  남: "MALE",
  남성: "MALE",
  남자: "MALE",
  F: "FEMALE",
  FEMALE: "FEMALE",
  여: "FEMALE",
  여성: "FEMALE",
  여자: "FEMALE",
};

/**
 * 저장된 성별 문자열을 정규값으로 바꾼다. 알 수 없는 값·빈 값은 `null`.
 * (한글은 `toUpperCase()`의 영향을 받지 않으므로 영문·한글을 같은 표에서 조회할 수 있다.)
 */
export function normalizeGender(raw?: string | null): NormalizedGender | null {
  if (!raw) {
    return null;
  }
  return GENDER_LOOKUP[raw.trim().toUpperCase()] ?? null;
}

/**
 * 화면용 긴 표기 — `"남성"` · `"여성"`. 판정 불가 시 `fallback`(기본 `"-"`).
 */
export function formatGender(raw?: string | null, fallback = "-"): string {
  const normalized = normalizeGender(raw);
  if (!normalized) {
    return fallback;
  }
  return normalized === "MALE" ? "남성" : "여성";
}

/**
 * 화면용 짧은 표기 — `"남"` · `"여"`. 좁은 목록에서 쓴다. 판정 불가 시 `fallback`(기본 `"-"`).
 */
export function formatGenderShort(raw?: string | null, fallback = "-"): string {
  const normalized = normalizeGender(raw);
  if (!normalized) {
    return fallback;
  }
  return normalized === "MALE" ? "남" : "여";
}
